import React, { FC, useState } from 'react'
import {
  CheckIcon,
  EyeIcon,
  PlusIcon,
  ClockIcon,
  TemplateIcon,
  PlayIcon
} from '@heroicons/react/outline'
import { PhotographIcon } from '@heroicons/react/solid'
import { useHistory } from 'react-router-dom'
import usePricing from '../../redux/pricing/usePricing'
import useCart from '../../redux/shopping-cart/useCart'
import { formatVideoLength } from '../utils/formatVideoLength'
import { CourseType } from '../../types'
import { StyledCoursePreviewItem, getCourseUrl, getCourseVideoLength } from '.'
import { CourseTrailerModal } from '../Modals/CourseTrailerModal'
import { getCourseLessonCount } from './utils/getCourseLessonCount'
import { getCourseCompletedPercentage } from './utils/getCourseCompletedPercentage'
import { CartPrice } from '../Cart'
import useSegment from '../utils/useSegment'
import { ShareOptions } from './ShareOptions'

type Props = {
  course: CourseType;
  isOwned?: boolean;
}

const CoursePreviewItem: FC<Props> = ({ course, isOwned }) => {
  const { track } = useSegment()
  const { actions: { getPriceData } } = usePricing()
  const { actions: { idInCart, addToCart } } = useCart()

  const history = useHistory()
  const coursePrice = getPriceData('course')
  const inCart = idInCart(course._id)
  const lessonsCount = getCourseLessonCount(course)
  const completedPercentage = getCourseCompletedPercentage(course, true)
  const [courseTrailerShown, setCourseTrailerShown] = useState(false)

  const handleAddToCart = (e: any) => {
    e.stopPropagation()
    e.preventDefault()

    if (!idInCart(course._id)) {
      return addToCart({ itemData: course, type: 'course' }, 'course list')
    }

    history.push('/checkout')
  }

  const handleShowTrailer = (e) => {
    e.preventDefault()
    setCourseTrailerShown(true)

    track('Opened Trailer Modal', {
      id: course._id,
      title: course.title
    })
  }

  // Track when the user shares the course's URL
  const trackShare = (type: string) => {
    track('Shared Course', {
      id: course._id,
      title: course.title,
      share_type: type
    })
  }

  return (
    <>
      <StyledCoursePreviewItem to={getCourseUrl(course, isOwned)} isOwned={isOwned}>
        <div className="course-image">
          {!isOwned && (
            <div className="course-preview-prompt">
              <button className="preview-btn">
                <EyeIcon />
                <span>Preview</span>
              </button>
            </div>
          )}
          {course.coverImage && <img src={course.coverImage.fileHttpLink} alt="" />}
          {!course.coverImage && <PhotographIcon className="empty-icon" />}
        </div>
        <div className="course-details">
          <span className="course-title">{course.title}</span>
          <span className="course-description-preview">{course.previewDescription}</span>
          <ul className="course-stats">
            <li>
              <ClockIcon />
              <span>{formatVideoLength(getCourseVideoLength(course))}</span>
            </li>
            <li>
              <TemplateIcon />
              <span>
                {lessonsCount} Lesson{lessonsCount !== 1 && 's'}
              </span>
            </li>
          </ul>
          {!isOwned && (
            <div className="course-actions">
              {course?.teaser?.svid && (
                <button className="course-preview-btn" onClick={(e) => handleShowTrailer(e)}>
                  <PlayIcon />
                  Trailer
                </button>
              )}
              <ShareOptions
                shareURL={`${window.location}/${course.slug}/preview`}
                onShare={(type) => trackShare(type)}
              />
            </div>
          )}
          {isOwned && (
            <div className="course-progress">
              <label htmlFor="course-progress">
                {completedPercentage.toFixed(0) === '0'
                  ? 'NOT STARTED'
                  : `${completedPercentage.toFixed(0)}% COMPLETE`}
              </label>
              <progress id="course-progress" max="100" value={completedPercentage}>
                {completedPercentage.toFixed(0)}%
              </progress>
            </div>
          )}

          {!isOwned && !inCart && coursePrice && (
            <button className="add-to-cart-btn" onClick={(e) => handleAddToCart(e)}>
              <PlusIcon />
              <span>Add to Cart for</span>
              <CartPrice item={coursePrice} />
            </button>
          )}
          {!isOwned && inCart && (
            <button className="add-to-cart-btn" onClick={(e) => handleAddToCart(e)}>
              <CheckIcon className="check-icon" />
              <span>In Cart</span>
            </button>
          )}
        </div>
      </StyledCoursePreviewItem>
      <CourseTrailerModal
        isOpen={courseTrailerShown}
        course={{
          ...course,
          isOwned
        }}
        onClose={() => setCourseTrailerShown(false)}
      />
    </>
  )
}

export default CoursePreviewItem
